import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { http } from "@/lib/api";

/**
 * BillingSuccess — landing page Stripe redirects to after a successful
 * checkout for the Professional plan signup flow.
 *
 * Implements the polling-loop pattern from the Stripe playbook:
 * the frontend repeatedly hits /billing/checkout-status/{id} until
 * payment_status === "paid" (or the session expires / a hard error
 * shows). We rely on the polling rather than the webhook because the
 * preview environment isn't always reachable from Stripe's webhook
 * IPs — the webhook handler still runs in production as a backstop.
 */
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 8;

export default function BillingSuccess() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const sessionId = params.get("session_id") || "";

    const [state, setState] = useState({ status: "checking", err: "" });

    const poll = useCallback(
        async (attempt) => {
            if (!sessionId) {
                setState({ status: "missing", err: "No session id in URL." });
                return;
            }
            try {
                const r = await http.get(`/billing/checkout-status/${sessionId}`);
                const ps = r?.data?.payment_status;
                if (ps === "paid") {
                    setState({ status: "paid", err: "" });
                    setTimeout(() => navigate("/", { replace: true }), 1800);
                    return;
                }
                if (r?.data?.status === "expired") {
                    setState({ status: "expired", err: "Checkout session expired." });
                    return;
                }
                if (attempt >= MAX_POLL_ATTEMPTS) {
                    setState({
                        status: "timeout",
                        err: "We didn't see your payment confirmation in time. Check your email — if Stripe charged you, your tenant will be promoted shortly.",
                    });
                    return;
                }
                setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
            } catch (e) {
                const msg = e?.response?.data?.detail || "Status check failed.";
                setState({ status: "error", err: String(msg) });
            }
        },
        [sessionId, navigate],
    );

    useEffect(() => {
        poll(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <CenterCard testid="billing-success-page">
            {state.status === "paid" ? (
                <>
                    <CheckCircle2 size={36} className="text-[#10b981]" />
                    <h1 className="mt-5 font-display text-2xl font-bold tracking-tight">
                        You're in.
                    </h1>
                    <p className="mt-2 text-sm text-white/65">
                        Payment received — your Professional workspace is active.
                        Taking you to the dashboard…
                    </p>
                </>
            ) : state.status === "checking" ? (
                <>
                    <Loader2 size={32} className="animate-spin text-[#00C2FF]" />
                    <h1 className="mt-5 font-display text-2xl font-bold tracking-tight">
                        Confirming your payment…
                    </h1>
                    <p className="mt-2 text-sm text-white/65">
                        Hang tight — we're talking to Stripe.
                    </p>
                </>
            ) : (
                <>
                    <AlertTriangle size={32} className="text-[#f59e0b]" />
                    <h1 className="mt-5 font-display text-2xl font-bold tracking-tight">
                        {state.status === "expired"
                            ? "Session expired"
                            : state.status === "timeout"
                                ? "Still processing"
                                : "Couldn't confirm payment"}
                    </h1>
                    <p className="mt-2 text-sm text-white/65">{state.err}</p>
                    <Link
                        to="/"
                        data-testid="billing-success-fallback"
                        className="mt-7 inline-flex items-center justify-center rounded-lg bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.22em] text-[#03122b] transition hover:bg-white/90"
                    >
                        Go to dashboard
                    </Link>
                </>
            )}
        </CenterCard>
    );
}

function CenterCard({ children, testid }) {
    return (
        <div
            className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#03122b] px-6 text-center text-white"
            data-testid={testid}
        >
            <div className="pointer-events-none absolute inset-0 grid-bg opacity-[0.12]" />
            <div className="pointer-events-none absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full bg-[#00C2FF]/12 blur-[160px]" />
            <div className="pointer-events-none absolute -right-40 bottom-1/4 h-[420px] w-[420px] rounded-full bg-[#5b5ef0]/14 blur-[160px]" />
            <div className="relative z-10 w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-9 backdrop-blur-xl">
                {children}
            </div>
        </div>
    );
}
